import { useCallback, useEffect, useState } from "react";
import {
  fetchGovernanceAudit,
  fetchGovernanceAuditPage,
  type GovernanceAuditEntry,
} from "@/lib/api/organizations";

export interface AuditFeed {
  entries: GovernanceAuditEntry[];
  /// `null` until the first load settles, then `false` when this caller may not
  /// read the log — the section hides rather than showing an error.
  available: boolean | null;
  hasMore: boolean;
  loadingMore: boolean;
  error: string | null;
  loadMore: () => void;
}

/// One page of governance events, plus paging. `spaceId` narrows to a single
/// space; omit it for the whole org.
export function useGovernanceAudit(
  orgId: string | null,
  spaceId?: string,
  pageSize = 100,
): AuditFeed {
  const [entries, setEntries] = useState<GovernanceAuditEntry[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setAvailable(false);
      return;
    }
    let live = true;
    setAvailable(null);
    setError(null);
    void fetchGovernanceAudit(orgId, { spaceId, limit: pageSize }).then(
      (page) => {
        if (!live) return;
        setEntries(page?.entries ?? []);
        setCursor(page?.next_cursor ?? null);
        setAvailable(page !== null);
      },
    );
    return () => {
      live = false;
    };
  }, [orgId, spaceId, pageSize]);

  const loadMore = useCallback(() => {
    if (!orgId || cursor === null || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    fetchGovernanceAuditPage(orgId, {
      spaceId,
      before: cursor,
      limit: pageSize,
    })
      .then((page) => {
        // Keyset paging cannot overlap, but a re-fetch of the same cursor
        // could — dedupe so it stays harmless.
        setEntries((prev) => {
          const seen = new Set(prev.map((e) => e.id));
          return [...prev, ...page.entries.filter((e) => !seen.has(e.id))];
        });
        setCursor(page.next_cursor);
      })
      .catch(() => setError("Could not load more events. Try again."))
      .finally(() => setLoadingMore(false));
  }, [orgId, spaceId, cursor, loadingMore, pageSize]);

  return {
    entries,
    available,
    hasMore: cursor !== null,
    loadingMore,
    error,
    loadMore,
  };
}
